<script lang="ts">
  import { getRarity } from '$lib/utils/accessories'
  import { fade, type TransitionConfig } from 'svelte/transition'
  import CloseIcon from '~icons/tabler/x'
  import AccessoryRevealScene from './AccessoryRevealScene.svelte'
  import AchievementRevealScene from './AchievementRevealScene.svelte'
  import type { RevealItem, RevealQueue } from './reveal-queue.svelte'

  let { queue }: { queue: RevealQueue } = $props()

  const rarityOf = (item: RevealItem) =>
    item.kind === 'accessory'
      ? getRarity(item.accessory)
      : item.achievement.rarity

  const sceneOut = (_: Element): TransitionConfig => ({
    duration: 250,
    css: (t: number) => `
      transform: scale(${0.92 + 0.08 * t});
      opacity: ${t};
    `,
  })
</script>

<svelte:window
  onkeydown={(event) => {
    if (queue.visible && event.key === 'Escape') queue.close()
  }}
/>

{#if queue.visible && queue.current}
  {@const current = queue.current}
  <div
    class="reveal-stage rarity-{rarityOf(current)}"
    role="dialog"
    aria-modal="true"
    aria-label="New reward"
    transition:fade={{ duration: 300 }}
  >
    <div class="dim"></div>

    {#key `${current.kind}:${current.id}`}
      <div class="scene-holder" out:sceneOut>
        {#if current.kind === 'accessory'}
          <AccessoryRevealScene
            accessory={current.accessory}
            onContinue={() => queue.dismissCurrent()}
            onEquip={() => queue.equipCurrent()}
          />
        {:else}
          <AchievementRevealScene
            achievement={current.achievement}
            onContinue={() => queue.dismissCurrent()}
          />
        {/if}
      </div>
    {/key}

    {#if queue.total > 1}
      <div class="counter">{queue.position} of {queue.total}</div>
    {/if}
    <button
      class="close unstyled"
      onclick={() => queue.close()}
      aria-label="Close"
    >
      <CloseIcon />
    </button>
  </div>
{/if}

<style lang="postcss">
  .reveal-stage {
    position: fixed;
    z-index: var(--layer-top);
    isolation: isolate;
    inset: 0;

    /* The stage is deliberately dark in both themes: rarity colors only glow
       on dark. --reveal-hue is lifted toward white for luminous accents;
       --reveal-ring is the raw saturated anchor for crisp solid elements
       (medal ring, card frame, stamp chip, beam edges). */
    --reveal-hue: oklch(87% 0.02 260);
    --reveal-ring: oklch(80% 0.01 260);
    --slow: 1;
    &.rarity-rare {
      --reveal-hue: color-mix(in oklab, var(--rarity-rare) 70%, white);
      --reveal-ring: var(--rarity-rare);
    }
    &.rarity-epic {
      --reveal-hue: color-mix(in oklab, var(--rarity-epic) 65%, white);
      --reveal-ring: var(--rarity-epic);
    }
    &.rarity-legendary {
      --reveal-hue: color-mix(in oklab, var(--rarity-legendary) 80%, white);
      --reveal-ring: var(--rarity-legendary);
    }
    &.rarity-sponsored {
      --reveal-hue: color-mix(in oklab, var(--rarity-sponsored) 70%, white);
      --reveal-ring: var(--rarity-sponsored);
    }
  }
  .dim {
    position: absolute;
    backdrop-filter: blur(14px);
    inset: 0;
    background: oklch(14% 0.015 270 / 0.92);
  }
  .scene-holder {
    position: absolute;
    inset: 0;
  }
  .counter {
    position: absolute;
    top: 2rem;
    left: 50%;
    transform: translateX(-50%);
    border-radius: var(--radius-full);
    background: oklch(100% 0 0 / 0.08);
    padding: 0.25rem 0.875rem;
    color: oklch(90% 0.01 270);
    font-size: var(--scale-000);
  }
  .close {
    position: absolute;
    top: 2rem;
    right: 3rem;
    color: oklch(90% 0.01 270);
    font-size: 2rem;
  }
</style>
